import { supabase } from '../lib/supabase';

/**
 * Buying Guide Service - Handles all buying guide operations with Supabase
 * Converts between snake_case (database) and camelCase (React)
 */

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toCamelCase);
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toSnakeCase);
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
      result[snakeKey] = toSnakeCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

export const buyingGuideService = {
  /**
   * Get all buying guides
   */
  async getAllGuides() {
    try {
      const { data, error } = await supabase
        ?.from('buying_guides')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching buying guides:', error);
      throw error;
    }
  },

  /**
   * Get published buying guides only
   */
  async getPublishedGuides() {
    try {
      const { data, error } = await supabase
        ?.from('buying_guides')
        ?.select('*')
        ?.eq('is_published', true)
        ?.order('published_at', { ascending: false });

      if (error) throw error;

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching published guides:', error);
      throw error;
    }
  },

  /**
   * Get buying guide by ID
   */
  async getGuideById(guideId) {
    try {
      const { data, error } = await supabase
        ?.from('buying_guides')
        ?.select('*')
        ?.eq('id', guideId)
        ?.single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching buying guide:', error);
      throw error;
    }
  },

  /**
   * Get buying guide by slug
   */
  async getGuideBySlug(slug) {
    try {
      const { data, error } = await supabase
        ?.from('buying_guides')
        ?.select('*')
        ?.eq('slug', slug)
        ?.single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching buying guide by slug:', error);
      throw error;
    }
  },

  /**
   * Create new buying guide
   */
  async createGuide(guideData) {
    try {
      const snakeCaseData = toSnakeCase(guideData);
      
      const { data, error } = await supabase
        ?.from('buying_guides')
        ?.insert([snakeCaseData])
        ?.select()
        ?.single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating buying guide:', error);
      throw error;
    }
  },

  /**
   * Update buying guide
   */
  async updateGuide(guideId, guideData) {
    try {
      const snakeCaseData = toSnakeCase(guideData);
      
      const { data, error } = await supabase
        ?.from('buying_guides')
        ?.update(snakeCaseData)
        ?.eq('id', guideId)
        ?.select()
        ?.single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating buying guide:', error);
      throw error;
    }
  },

  /**
   * Delete buying guide
   */
  async deleteGuide(guideId) {
    try {
      const { error } = await supabase
        ?.from('buying_guides')
        ?.delete()
        ?.eq('id', guideId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting buying guide:', error);
      throw error;
    }
  },

  /**
   * Publish/unpublish buying guide
   */
  async togglePublishStatus(guideId, isPublished) {
    try {
      const updateData = {
        is_published: isPublished,
        published_at: isPublished ? new Date()?.toISOString() : null
      };

      const { data, error } = await supabase
        ?.from('buying_guides')
        ?.update(updateData)
        ?.eq('id', guideId)
        ?.select()
        ?.single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error toggling publish status:', error);
      throw error;
    }
  },

  /**
   * Increment view count
   */
  async incrementViewCount(guideId) {
    try {
      const { error } = await supabase?.rpc('increment_guide_views', {
        guide_id: guideId
      });

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error incrementing view count:', error);
      // Don't throw error for view count failures
      return { success: false };
    }
  },

  /**
   * Get guides by category
   */
  async getGuidesByCategory(category) {
    try {
      const { data, error } = await supabase
        ?.from('buying_guides')
        ?.select('*')
        ?.eq('category', category)
        ?.eq('is_published', true)
        ?.order('published_at', { ascending: false });

      if (error) throw error;

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching guides by category:', error);
      throw error;
    }
  },

  /**
   * Search buying guides
   */
  async searchGuides(searchTerm) {
    try {
      const { data, error } = await supabase
        ?.from('buying_guides')
        ?.select('*')
        ?.or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%,summary.ilike.%${searchTerm}%`)
        ?.eq('is_published', true)
        ?.order('published_at', { ascending: false });

      if (error) throw error;

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error searching guides:', error);
      throw error;
    }
  }
};

export default buyingGuideService;